import { type FC, forwardRef } from 'react'
import { type AnyMaskedOptions } from 'imask'

import { HUMAN_DATE_FORMAT } from '@Constants/date'
import AdditionalMessage from '@DS/components/components/tooltip/additionalMessage/AdditionalMessage'
import { type DescriptionMessage } from '@DS/components/components/tooltip/additionalMessage/AdditionalMessage.types'
import ConditionalWrapper from '@DS/components/components/utils/wrapper/ConditionalWrapper'
import useUuid from '@Hooks/useUuid'
import { type DateFormat } from '@Types/date'

import BaseMaskedInput from '../_internal/BaseMaskedInput'
import { type BaseInputProps } from '../BaseInput.types'
import { formatDateInput, getDateMaskPatternByDateFormat, parseDateInput } from '../dateInputs.utils'
import { createMaskedRange } from '../ImaskMaskedRange.utils'

import { type CalendarButtonProps } from './_internal/CalendarButton'

export type InputDateProps = BaseInputProps & {
  calendarIcon?: CalendarButtonProps
  dateFormat?: DateFormat
  descriptionMessage?: DescriptionMessage
  isEndAdornmentHidden?: boolean
}

const InputDate: FC<InputDateProps> = forwardRef<HTMLDivElement, InputDateProps>(
  (
    {
      descriptionMessage,
      isError = false,
      name,
      onChange,
      dateFormat = HUMAN_DATE_FORMAT,
      calendarIcon,
      value,
      readOnly,
      ...rest
    },
    ref,
  ) => {
    const id = useUuid()

    const formatDate = (date: Date) => formatDateInput(date, dateFormat)
    const parseDate = (str: string) => parseDateInput(str, dateFormat)
    const pattern = getDateMaskPatternByDateFormat(dateFormat)
    const hasDay = dateFormat === HUMAN_DATE_FORMAT

    return (
      <ConditionalWrapper
        condition={!!descriptionMessage}
        wrapper={(children) => (
          <AdditionalMessage
            descriptionMessage={descriptionMessage as DescriptionMessage}
            id={name}
            tooltipPlacement="bottom-end"
            isFullWidth
          >
            {children}
          </AdditionalMessage>
        )}
      >
        <BaseMaskedInput
          {...rest}
          name={name}
          id={id}
          blocks={{
            Y: createMaskedRange({ from: 1900, to: 2100 }),
            m: createMaskedRange({ from: 1, to: 12 }),
            ...(hasDay && {
              d: createMaskedRange({ from: 1, to: 31 }) satisfies AnyMaskedOptions,
            }),
          }}
          onChange={onChange}
          isError={isError}
          onFormat={formatDate}
          calendarIcon={calendarIcon}
          parse={parseDate}
          pattern={pattern}
          value={value}
          readOnly={readOnly}
          ref={ref}
        />
      </ConditionalWrapper>
    )
  },
)

export default InputDate
